import { PlayFilledIcon } from '@jouzen/ecom-components/icons/PlayFilledIcon';
import { Modal } from '@jouzen/ecom-components/v3/Modal';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import RichText from '@/app/components/RichText';

import type { SizeGuideModalStepProps } from './SizeGuideModalStep';
import SizeGuideModalStep from './SizeGuideModalStep';

export interface SizeGuideModalProps {
  readonly description: MessageKey;
  readonly isOpen: boolean;
  readonly onWatchVideoClick: () => void;
  readonly steps: Pick<SizeGuideModalStepProps, 'content' | 'cta' | 'title'>[];
  readonly title: MessageKey;
  readonly toggleModal: (isOpen: boolean) => void;
}

const SizeGuideModal = ({
  description,
  isOpen,
  onWatchVideoClick,
  steps,
  title,
  toggleModal,
}: SizeGuideModalProps) => {
  const t = useTranslations();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={toggleModal}
      padding="large"
      rounded
      size="large"
    >
      <div
        className="lg:flex lg:justify-between"
        data-cy="size-guide-modal-wrapper"
      >
        <div className="max-lg:mb-9 max-lg:border-b max-lg:border-sandstone-400 max-lg:pb-9 lg:w-[35%]">
          <Typography
            className="mb-5 leading-tighter"
            Element="h2"
            variant="h4"
          >
            {t(title)}
          </Typography>
          <Typography className="mb-5">
            <RichText messageKey={description} />
          </Typography>
          <button
            className="flex items-center gap-x-4 text-lg leading-none font-bold text-sandstone-500"
            data-cy="size-guide-modal-video-cta"
            onClick={onWatchVideoClick}
            type="button"
          >
            <span
              aria-hidden="true"
              className="flex size-8 items-center justify-center rounded-full bg-sandstone-500 text-sandstone-200"
            >
              <PlayFilledIcon height={10} width={10} />
            </span>
            <span>{t('watch_the_video')}</span>
          </button>
        </div>
        <div className="lg:w-[55%]">
          <ol>
            {steps.map((step, i) => (
              <SizeGuideModalStep {...step} index={i} key={step.title} />
            ))}
          </ol>
        </div>
      </div>
    </Modal>
  );
};

export default SizeGuideModal;
